<template>
    <div class="container course-screen min-full d-flex flex-column align-items-center justify-content-center">
        <b-link class="back" to="/courses/"><svg-icon icon="back" /></b-link>
        <template v-if="course && !loading">
            <div class="w-100">
                <div v-if="isIntro" class="course-introduction">
                    <div class="course-introduction-box" :class="textDir">
                        <img :src="course.course_image" style="max-width: 100%;" />
                        <h1 class="mt-4 mb-4 pl-4 pr-4 text-center">{{course.name}}</h1>
                        <p class="'text-area pl-4 pr-4" v-html="course.description" />
                    </div>
                    <button
                        type="button"
                        @click="startCourse"
                        :disabled="startingCourse"
                        class="btn p-3 mt-3 btn-primary btn-block d-inline-flex align-items-center justify-content-center"
                    >
                        <b-spinner v-if="startingCourse" small class="mr-2" />
                        {{$t('startCourse')}}
                    </button>
                </div>
                <div v-else>
                    <h1>{{currentModule.name}}</h1>
                    <div :class="textDir">
                        <h2 class="text-center">{{currentLesson.page_name}}</h2>
                        <CourseBlock
                            v-for="(block, blockIndex) in currentLesson.page_data"
                            :key="'' + moduleIdx + lessonIdx + blockIndex"
                            :data="currentLesson.page_data[blockIndex]"
                            :module="moduleIdx"
                            :textDir="textDir"
                            :lesson="lessonIdx"
                            :ref="blockIndex"
                            :id="blockIndex"
                            :missed="missed"
                            :notInteracted="notInteractedWith.includes(blockIndex)"
                            :submit-handler="submitAnswers"
                            @interacted="interacted"
                        />
                    </div>
                    <div class="d-flex justify-content-between">
                        <b-button
                            variant="primary"
                            block
                            v-if="!isIntro"
                            @click="prevLesson"
                            :disabled="completingLesson"
                            class="p-3 mt-3 mx-3 d-inline-flex align-items-center justify-content-center"
                        >
                            {{$t('prevLesson')}}
                        </b-button>
                        <b-button
                            variant="primary"
                            block
                            v-if="!(moduleIdx === course.data.length - 1 && lessonIdx === currentModule.pages.length - 1)"
                            @click="nextLesson"
                            :disabled="completingLesson"
                            class="p-3 mt-3 mx-3 d-inline-flex align-items-center justify-content-center"
                        >
                            <b-spinner v-if="completingLesson" small class="mr-2" />
                            {{$t('nextLesson')}}
                        </b-button>
                        
                        <b-button
                            v-else
                            variant="primary"
                            :disabled="finishingCourse"
                            @click="finishCourse"
                            block
                            class="p-3 mt-3 mx-3 d-inline-flex align-items-center justify-content-center"
                        >
                            <b-spinner v-if="finishingCourse" small class="mr-2" />
                            {{$t('finish')}}
                        </b-button>
                    </div>
                </div>
            </div>
            <div class="question__selector tall shadow" :class="{'show': showNav}">
                <button class="question__selector__close" @click="showNav = false"><svg-icon icon="close" width="18" height="18" /></button>
                <div class="question__selector__inner">
                    <h2>{{ $t('lessonMenu') }}</h2>
                    <ul class="question__selector__list">
                        <ul class="question__selector__lessonList">
                            <li class="question__selector__courseItem" @click="isIntro = true">
                                {{ $t('courseIntro') }}
                            </li>
                        </ul>
                        <li v-for="(module, moduleIndex) in course.data" :key="'module' + module.id" class="pb-2">
                            <h5 class="mb-1">{{ module.name }}</h5>
                            <ul class="question__selector__lessonList">
                                <li 
                                    v-for="(lesson, lessonIndex) in module.pages"
                                    :key="'lesson' + lesson.id"
                                    class="question__selector__courseItem"
                                    @click="navLesson(lesson.id, lessonIndex, moduleIndex)"
                                    :class="canGoTo(lesson.id) ? '': 'disabled'"
                                >
                                    {{ lesson.page_name }}
                                </li>
                            </ul>
                        </li>
                    </ul>
                </div>
            </div>
            <button class="course__menuBtn" id="courseMenuBtn" @click="toggleMenu()"><svg-icon icon="menu" /></button>
            <b-tooltip target="courseMenuBtn" :title="$t('lessonMenu')" placement="topleft" ref="menuBtnLabel"></b-tooltip>
        </template>
        <LoadingSpinner v-if="loading" />
    </div>
</template>

<script>

import CourseService from '@/services/CourseService';
import CourseBlock from '@/components/courses/CourseBlock.vue';
import BrandingMixin from '@/mixins/BrandingMixin';
import SvgIcon from '@/components/SvgIcon';
import LoadingSpinner from '@/components/loader/Loader';
import { isAuthExpiredError } from '@/common/auth';
import { getDetailNetworkToast, notifyError } from '@/common/network';

export default {
    name: 'CourseScreen',
    components: {
        CourseBlock,
        SvgIcon,
        LoadingSpinner
    },
    data(){
        return {
            course: null,
            id: this.$route.params.id,
            isIntro: true,
            moduleIdx: 0,
            lessonIdx: 0,
            moduleIdxTrace: 0,
            lessonIdxTrace: 0,
            courseDataReturn: [],
            loading: true,
            courseRequestData: null,
            missed: false,
            notInteractedWith: [],
            showNav: false,
            startingCourse: false,
            completingLesson: false,
            finishingCourse: false,
        }
    },
    mixins: [
        BrandingMixin
    ],
    async mounted(){
        const loaded = await this.getCourse();
        if (!loaded || !this.course) {
            return;
        }

        const providerID = this.course.provider_id || this.$store.state.site.id;
        this.getCompanyBranding(providerID);
    },
    computed: {
        textDir() {
            if (this.course.is_rtl !== undefined){
                if (this.course.is_rtl){
                    return 'rtl';
                } else {
                    return 'ltr';
                }
            } else {
                return '';
            }
        },
        currentModule() {
            if (!this.loading){
                return this.course.data[this.moduleIdx];
            }
            return null;
        },
        currentLesson() {
            if (!this.loading){
                return this.currentModule.pages[this.lessonIdx];
            }
            return null;
        },
    },
    methods: {
        showToast(titleKey, textKey, options = {}) {
            this.$bvToast.toast(this.$t(textKey, options.textArgs || []), {
                title: this.$t(titleKey, options.titleArgs || []),
                toaster: 'b-toaster-top-center',
                autoHideDelay: options.autoHideDelay || 5000,
                appendToast: false,
                variant: options.variant || 'primary',
                solid: true,
                id: options.id,
            });
        },
        showErrorToast(titleKey, textKey, options = {}) {
            this.showToast(titleKey, textKey, {
                ...options,
                variant: 'danger',
            });
        },
        showLoadFailureToast() {
            const toastKey = getDetailNetworkToast('course');
            this.showErrorToast(toastKey + 'Title', toastKey + 'Text');
        },
        showActionFailureToast(messageKey) {
            this.showErrorToast('requestFailedTitle', messageKey, {
                autoHideDelay: 5000,
            });
        },
        async getCourse() {
            try {
                const response = await CourseService.getCourse(this.id, false);
                this.courseRequestData = response;
                this.course = response.course;
                this.courseDataReturn = response.course.response_data;
                let data = response.course.response_data;
                this.course.data = this.parseAnswers(data);
                this.setInitialCoursePosition();
                document.title = this.course.name + ' | ' + this.$store.state.site.shortTitle;
                this.loading = false;
                this.checkNotInteractedWith();
                return true;
            } catch(error) {
                if (isAuthExpiredError(error)) {
                    return false;
                }
                if (error?.status !== 404) {
                    notifyError(error, {
                        source: {
                            screen: 'CourseScreen',
                            action: 'getCourse',
                            courseId: this.id,
                        },
                    });
                }
                this.showLoadFailureToast();
                this.$router.push('/courses');
                return false;
            }
        },
        setInitialCoursePosition() {
            if (!this.course) {
                return;
            }

            const hasCompletedCourse = ['Submitted', 'Failed', 'Passed'].includes(this.course.status);
            const pageIds = Array.isArray(this.course.page_ids) ? this.course.page_ids : [];

            if (hasCompletedCourse || pageIds.length === 0) {
                return;
            }

            const lastCompletedLessonId = parseInt(pageIds.at(-1), 10);
            if (Number.isNaN(lastCompletedLessonId)) {
                return;
            }

            const nextLessonId = this.getNextLessonID(lastCompletedLessonId);
            const resumeLessonId = nextLessonId ?? lastCompletedLessonId;
            const index = this.findIndexFromLesson(resumeLessonId);

            if (index !== false) {
                this.isIntro = false;
                this.moduleIdx = index.module;
                this.lessonIdx = index.lesson;
                this.moduleIdxTrace = index.module;
                this.lessonIdxTrace = index.lesson;
            }
        },

        async startCourse() {
            if (this.startingCourse) {
                return;
            }
            if (!this.courseRequestData.course.is_started){
                const nextCourseRequestData = JSON.parse(JSON.stringify(this.courseRequestData));
                nextCourseRequestData.course.is_started = 1;
                this.startingCourse = true;
                try {
                    await CourseService.startCourse(this.course.id, nextCourseRequestData);
                    this.courseRequestData.course.is_started = 1;
                } catch (error) {
                    notifyError(error, {
                        source: {
                            screen: 'CourseScreen',
                            action: 'startCourse',
                            courseId: this.course.id,
                        },
                    });
                    this.showActionFailureToast('startCourseFailed');
                    return;
                } finally {
                    this.startingCourse = false;
                }
            }
            this.isIntro = false;
            window.scrollTo(0,0);
            this.checkNotInteractedWith();
        },
        prevLesson() {
            if (this.completingLesson) {
                return;
            }
            if (this.moduleIdx === 0 && this.lessonIdx === 0){
                this.isIntro = true;
            } else {
                let newLesson = this.lessonIdx - 1;
                let newModule = null;
                if (this.lessonIdx === 0) {
                    newModule = this.moduleIdx - 1;
                    newLesson = this.course.data[this.moduleIdx - 1].pages.length - 1;
                }
                this.goToLesson(newLesson, newModule);
            }
        },
        async nextLesson(click) {
            if (this.completingLesson) {
                return;
            }
            if (this.notInteractedWith.length){
                if (click){
                    this.missed = true;
                }
                this.$refs[this.notInteractedWith[0]][0].$el.scrollIntoView({
                    behavior: 'smooth'
                });
            } else {
                this.missed = false;
                const nextCourseRequestData = JSON.parse(JSON.stringify(this.courseRequestData));
                if (!nextCourseRequestData.course.page_ids){
                    nextCourseRequestData.course.page_ids = [];
                }
                if (nextCourseRequestData.course.page_ids.indexOf('' + this.currentLesson.id) === -1){
                    nextCourseRequestData.course.page_ids.push('' + this.currentLesson.id);
                }

                this.completingLesson = true;
                try {
                    await CourseService.completeLesson(this.course.id, this.currentLesson.id, nextCourseRequestData);
                    this.courseRequestData.course.page_ids = nextCourseRequestData.course.page_ids;

                    let newLesson  = this.lessonIdx + 1;
                    let newModule = null;

                    if (this.lessonIdx === this.currentModule.pages.length - 1) {
                        newLesson = 0;
                        newModule = this.moduleIdx + 1;
                    }
                    this.goToLesson(newLesson, newModule);
                } catch (error) {
                    notifyError(error, {
                        source: {
                            screen: 'CourseScreen',
                            action: 'completeLesson',
                            courseId: this.course.id,
                            lessonId: this.currentLesson.id,
                        },
                    });
                    this.showActionFailureToast('completeLessonFailed');
                    this.completingLesson = false;
                }
            }
        },
        goToLesson(newLesson, newModule) {
            if (newModule !== null) {
                this.moduleIdx = newModule;
            }
            this.lessonIdx = newLesson;
            if (this.moduleIdx >= this.moduleIdxTrace) {
                if (this.moduleIdx > this.moduleIdxTrace || this.lessonIdx > this.lessonIdxTrace) {
                    this.lessonIdxTrace = this.lessonIdx;
                }
                this.moduleIdxTrace = this.moduleIdx;
            }
            this.completingLesson = false;
            this.checkNotInteractedWith();
            window.scrollTo(0,0);
        },
        navLesson(lessonID, lessonIndex, moduleIndex) {
            if (this.completingLesson) {
                return;
            }
            if (this.canGoTo(lessonID)){
                this.isIntro = false;
                this.goToLesson(lessonIndex, moduleIndex);
                this.showNav = false;
            }
        },
        async finishCourse() {
            if (this.finishingCourse) {
                return;
            }
            if (this.notInteractedWith.length){
                this.missed = true;
                this.$refs[this.notInteractedWith[0]][0].$el.scrollIntoView({
                    behavior: 'smooth'
                });
            } else {
                this.finishingCourse = true;
                try {
                    if (this.course.status === 'Not Submitted') {
                        const nextCourseRequestData = JSON.parse(JSON.stringify(this.courseRequestData));
                        await CourseService.finishCourse(this.course.id, nextCourseRequestData);
                        this.courseRequestData.course.status = 'Submitted';
                        this.course.status = 'Submitted';
                    }
                    this.$router.push('/courses');
                } catch (error) {
                    notifyError(error, {
                        source: {
                            screen: 'CourseScreen',
                            action: 'finishCourse',
                            courseId: this.course.id,
                        },
                    });
                    this.showActionFailureToast('finishCourseFailed');
                } finally {
                    this.finishingCourse = false;
                }
            }
        },
        toggleMenu(){
            this.$refs.menuBtnLabel.$emit('close');
            this.showNav = !this.showNav;
        },
        normalizeAnswerPayload(type, answers) {
            if (type === 'free-text') {
                return answers;
            }
            if (type === 'multiple') {
                return Array.isArray(answers) ? [...answers] : [];
            }
            return [answers];
        },
        setSubmittedAnswer(target, quizIndex, type, answers) {
            const normalizedAnswers = this.normalizeAnswerPayload(type, answers);
            if (type === 'free-text') {
                target[this.moduleIdx].pages[this.lessonIdx].page_data[quizIndex].answer = normalizedAnswers;
                return;
            }
            target[this.moduleIdx].pages[this.lessonIdx].page_data[quizIndex].questions[0].answers = normalizedAnswers;
        },
        async submitAnswers(quizIndex, type, answers){
            const nextCourseDataReturn = JSON.parse(JSON.stringify(this.courseDataReturn));
            this.setSubmittedAnswer(nextCourseDataReturn, quizIndex, type, answers);

            const nextCourseRequestData = JSON.parse(JSON.stringify(this.courseRequestData));
            nextCourseRequestData.course.response_data = nextCourseDataReturn;

            try {
                await CourseService.submitQuiz(this.$route.params.id, nextCourseDataReturn, nextCourseRequestData);
                this.setSubmittedAnswer(this.course.data, quizIndex, type, answers);
                this.courseDataReturn = nextCourseDataReturn;
                this.courseRequestData.course.response_data = nextCourseDataReturn;
                this.checkNotInteractedWith();
            } catch (error) {
                notifyError(error, {
                    source: {
                        screen: 'CourseScreen',
                        action: 'submitAnswers',
                        courseId: this.$route.params.id,
                        quizIndex,
                        type,
                    },
                });
                this.showActionFailureToast('submitAnswerFailed');
                throw error;
            }
        },
        interacted(id, identifier) {
            const type = this.course.data[this.moduleIdx].pages[this.lessonIdx].page_data[id].type;
            switch (type) {
                case 'tabs_block':
                    this.course.data[this.moduleIdx].pages[this.lessonIdx].page_data[id].tabs_information[identifier].viewed = true;
                    break;
                case 'hotspot_block':
                    this.course.data[this.moduleIdx].pages[this.lessonIdx].page_data[id].hotspot_information.hotspotTooltip[identifier].viewed = true;
                    break;
                case 'accordian_information_block':
                    this.course.data[this.moduleIdx].pages[this.lessonIdx].page_data[id].accordian_information[identifier].viewed = true;
                    break;
                case 'flash_information_block':
                    if (this.course.data[this.moduleIdx].pages[this.lessonIdx].page_data[id].cards){
                        this.course.data[this.moduleIdx].pages[this.lessonIdx].page_data[id].cards[identifier].viewed = true;
                    } else {
                        this.course.data[this.moduleIdx].pages[this.lessonIdx].page_data[id].flash_information.carouselCards[identifier].viewed = true;
                    }
                    break;
                case 'video':
                case 'video_block':
                    this.course.data[this.moduleIdx].pages[this.lessonIdx].page_data[id].viewed = true;
                    break;
                case 'embed':
                case 'embed_block':
                    this.course.data[this.moduleIdx].pages[this.lessonIdx].page_data[id].viewed = true;
                    break;
                case 'resource':
                case 'resource_block':
                    this.course.data[this.moduleIdx].pages[this.lessonIdx].page_data[id].resource_list[identifier].viewed = true;
                    break;
            }
            this.checkNotInteractedWith();
        },
        checkNotInteractedWith() {
            this.notInteractedWith = [];
            if ((this.course.status === 'Submitted' || this.course.status === 'Failed' || this.course.status === 'Passed') ||
                (Array.isArray(this.course.page_ids) && this.course.page_ids.includes('' + this.currentLesson.id))){
                return;
            }
            this.course.data[this.moduleIdx].pages[this.lessonIdx].page_data.forEach((block, blockIndex) => {
                if (['tabs_block', 'hotspot_block', 'accordian_information_block', 'flash_information_block', 'quiz', 'free-text', 'video', 'video_block', 'embed', 'embed_block', 'resource', 'resource_block'].includes(block.type)) {
                    switch (block.type) {
                        case 'tabs_block':
                            for (const tab of block.tabs_information) {
                                if (!tab.viewed){
                                    this.notInteractedWith.push(blockIndex)
                                    break;
                                }
                            }
                            break;
                        case 'hotspot_block':
                            for (const hotspot of block.hotspot_information.hotspotTooltip) {
                                if (!hotspot.viewed){
                                    this.notInteractedWith.push(blockIndex)
                                    break;
                                }
                            }
                            break;
                        case 'accordian_information_block':
                            for (const accordian of block.accordian_information) {
                                if (!accordian.viewed){
                                    this.notInteractedWith.push(blockIndex)
                                    break;
                                }
                            }
                            break;
                        case 'flash_information_block':
                            if (block.cards){
                                for (const flash of block.cards) {
                                    if (!flash.viewed){
                                        this.notInteractedWith.push(blockIndex)
                                    break;
                                    }
                                } 
                            } else {
                                for (const flash of block.flash_information.carouselCards) {
                                    if (!flash.viewed){
                                        this.notInteractedWith.push(blockIndex)
                                    break;
                                    }
                                } 
                            }
                            break;
                        case 'quiz':
                            if (!block.questions[0].answers){
                                this.notInteractedWith.push(blockIndex)
                                break;
                            }
                            break;
                        case 'free-text':
                            if (!block.answer){
                                this.notInteractedWith.push(blockIndex)
                                break;
                            }
                            break;
                        case 'video':
                        case 'video_block':
                            if (!block.viewed){
                                this.notInteractedWith.push(blockIndex)
                                break;
                            }
                            break;
                        case 'embed':
                        case 'embed_block':
                            if (!block.viewed){
                                this.notInteractedWith.push(blockIndex)
                                break;
                            }
                            break;
                        case 'resource':
                        case 'resource_block':
                            for (const resource of block.resource_list) {
                                if (!resource.viewed){
                                    this.notInteractedWith.push(blockIndex)
                                    break;
                                }
                            }
                            break;
                    }
                }
            });
        },
        findIndexFromLesson(lessonID) {
            let returnIDs = false;
            this.course.data.forEach((module, indexModule) => {
                module.pages.forEach((page, indexPage) => {
                    if (page.id === lessonID) {
                        returnIDs = {
                            module: indexModule,
                            lesson: indexPage,
                        };
                    }
                });
            });

            return returnIDs;
        },
        canGoTo(lessonID){
            if (this.course.status === 'Submitted' || this.course.status === 'Failed' || this.course.status === 'Passed'){
                return true;
            } else if (Array.isArray(this.course.page_ids) && this.course.page_ids.includes('' + lessonID)){
                return true;
            } else if (this.course.data[0].pages[0].id === lessonID && this.isIntro === false){
                return true;
            } else if (Array.isArray(this.course.page_ids) && lessonID === this.getNextLessonID(parseInt(this.course.page_ids.at(-1)))){
                return true;
            }
            return false;
        },
        getNextLessonID(lessonID) {
            for (let i = 0; i < this.course.data.length; i++) {
                let pages = this.course.data[i].pages;
                for (let j = 0; j < pages.length; j++) {
                    if (pages[j].id === lessonID) {
                        if (j + 1 < pages.length) {
                            return pages[j + 1].id;
                        } else {
                            if (i + 1 < this.course.data.length && this.course.data[i + 1].pages.length > 0) {
                                return this.course.data[i + 1].pages[0].id;
                            } else {
                                return null;
                            }
                        }
                    }
                }
            }
            return null;
        },
        parseAnswers(data) {
            if (data) {
                data.forEach(page => {
                    page.pages.forEach(module => {
                        if (module.answers && Object.keys(module.answers).length) {
                            module.page_data.map(block => {
                                Object.keys(module.answers).forEach(id => {
                                    if (block.type === 'quiz' && block.questions[0].id === id) {
                                        block.questionAnswers = module.answers[id];
                                    }
                                });
                            });
                        }
                    });
                });
            }
            return data;
        }
    }

}

</script>
